import UsageLogService from 'src/modules/usageLog/usageLogService';
import selectors from 'src/modules/usageLog/list/usageLogListSelectors';
import { i18n } from 'src/i18n';
import buildExporterFields from 'src/modules/usageLog/list/usageLogListExporterFields';
import Errors from 'src/modules/shared/error/errors';
import Message from 'src/view/shared/message';
import Exporter from 'src/modules/shared/exporter/exporter';

const prefix = 'USAGELOG_LIST';

// "Export All Filtered" fetches every row matching the active filter in bounded
// chunks (never a single `limit=count` request — that previously OOM'd the API)
// and caps the total to protect both the API and the browser tab.
const EXPORT_ALL_CHUNK_SIZE = 1000;
const EXPORT_ALL_MAX_ROWS = 50000;

const usageLogListActions = {
  FETCH_STARTED: `${prefix}_FETCH_STARTED`,
  FETCH_SUCCESS: `${prefix}_FETCH_SUCCESS`,
  FETCH_ERROR: `${prefix}_FETCH_ERROR`,

  RESETED: `${prefix}_RESETED`,
  TOGGLE_ONE_SELECTED: `${prefix}_TOGGLE_ONE_SELECTED`,
  TOGGLE_ALL_SELECTED: `${prefix}_TOGGLE_ALL_SELECTED`,
  CLEAR_ALL_SELECTED: `${prefix}_CLEAR_ALL_SELECTED`,

  PAGINATION_CHANGED: `${prefix}_PAGINATION_CHANGED`,
  SORTER_CHANGED: `${prefix}_SORTER_CHANGED`,

  EXPORT_STARTED: `${prefix}_EXPORT_STARTED`,
  EXPORT_SUCCESS: `${prefix}_EXPORT_SUCCESS`,
  EXPORT_ERROR: `${prefix}_EXPORT_ERROR`,

  EXPORT_ALL_STARTED: `${prefix}_EXPORT_ALL_STARTED`,
  EXPORT_ALL_SUCCESS: `${prefix}_EXPORT_ALL_SUCCESS`,
  EXPORT_ALL_ERROR: `${prefix}_EXPORT_ALL_ERROR`,

  doClearAllSelected() {
    return {
      type: usageLogListActions.CLEAR_ALL_SELECTED,
    };
  },

  doToggleAllSelected() {
    return {
      type: usageLogListActions.TOGGLE_ALL_SELECTED,
    };
  },

  doToggleOneSelected(id) {
    return {
      type: usageLogListActions.TOGGLE_ONE_SELECTED,
      payload: id,
    };
  },

  doReset: () => async (dispatch) => {
    dispatch({
      type: usageLogListActions.RESETED,
    });

    dispatch(usageLogListActions.doFetch());
  },

  doExport: () => async (dispatch, getState) => {
    try {
      const exporterFields = buildExporterFields();

      if (!exporterFields || !exporterFields.length) {
        throw new Error('exporterFields is required');
      }

      dispatch({
        type: usageLogListActions.EXPORT_STARTED,
      });

      // Export only the current page — the active page at the selected page
      // size (10–40) — by re-fetching it with the current limit/offset.
      // Keeps the request tiny (so it never strains the API) while giving a
      // real in-progress state for the button spinner.
      const response = await UsageLogService.list(
        selectors.selectFilter(getState()),
        selectors.selectOrderBy(getState()),
        selectors.selectLimit(getState()),
        selectors.selectOffset(getState()),
      );

      new Exporter(
        exporterFields,
        i18n('entities.usageLog.exporterFileName'),
      ).transformAndExportAsExcelFile(response.rows);

      dispatch({
        type: usageLogListActions.EXPORT_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: usageLogListActions.EXPORT_ERROR,
      });
    }
  },

  doExportAll: () => async (dispatch, getState) => {
    try {
      const exporterFields = buildExporterFields();

      if (!exporterFields || !exporterFields.length) {
        throw new Error('exporterFields is required');
      }

      dispatch({
        type: usageLogListActions.EXPORT_ALL_STARTED,
      });

      const filter = selectors.selectFilter(getState());
      const orderBy = selectors.selectOrderBy(getState());

      // Fetch the first chunk to learn the total count before committing to
      // pulling everything — lets us bail early if it exceeds the cap.
      const first = await UsageLogService.list(
        filter,
        orderBy,
        EXPORT_ALL_CHUNK_SIZE,
        0,
      );

      const count = first.count || 0;

      if (count > EXPORT_ALL_MAX_ROWS) {
        Message.error(
          i18n(
            'entities.usageLog.exportAll.tooMany',
            String(count),
            String(EXPORT_ALL_MAX_ROWS),
          ),
        );

        dispatch({
          type: usageLogListActions.EXPORT_ALL_ERROR,
        });

        return;
      }

      let rows = [...(first.rows || [])];

      // Pull the remaining pages sequentially (bounded by the cap above).
      for (
        let offset = EXPORT_ALL_CHUNK_SIZE;
        offset < count;
        offset += EXPORT_ALL_CHUNK_SIZE
      ) {
        const response = await UsageLogService.list(
          filter,
          orderBy,
          EXPORT_ALL_CHUNK_SIZE,
          offset,
        );

        rows = rows.concat(response.rows || []);
      }

      new Exporter(
        exporterFields,
        i18n('entities.usageLog.exporterFileName'),
      ).transformAndExportAsExcelFile(rows);

      dispatch({
        type: usageLogListActions.EXPORT_ALL_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: usageLogListActions.EXPORT_ALL_ERROR,
      });
    }
  },

  doChangePagination: (pagination) => async (
    dispatch,
    getState,
  ) => {
    dispatch({
      type: usageLogListActions.PAGINATION_CHANGED,
      payload: pagination,
    });

    dispatch(usageLogListActions.doFetchCurrentFilter());
  },

  doChangeSort: (sorter) => async (dispatch, getState) => {
    dispatch({
      type: usageLogListActions.SORTER_CHANGED,
      payload: sorter,
    });

    dispatch(usageLogListActions.doFetchCurrentFilter());
  },

  doFetchCurrentFilter: () => async (
    dispatch,
    getState,
  ) => {
    const filter = selectors.selectFilter(getState());
    const rawFilter = selectors.selectRawFilter(getState());
    dispatch(usageLogListActions.doFetch(filter, rawFilter, true));
  },

  doFetch: (filter?, rawFilter?, keepPagination = false) => async (
    dispatch,
    getState,
  ) => {
    try {
      dispatch({
        type: usageLogListActions.FETCH_STARTED,
        payload: { filter, rawFilter, keepPagination },
      });

      const response = await UsageLogService.list(
        filter,
        selectors.selectOrderBy(getState()),
        selectors.selectLimit(getState()),
        selectors.selectOffset(getState()),
      );

      dispatch({
        type: usageLogListActions.FETCH_SUCCESS,
        payload: {
          rows: response.rows,
          count: response.count,
        },
      });
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: usageLogListActions.FETCH_ERROR,
      });
    }
  },
};

export default usageLogListActions;
